import { lazy, Suspense } from "react"
import { useTranslation } from "react-i18next"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import { QueryProvider } from "@/providers/query-provider"
import { AuthProvider } from "@/providers/auth-provider"
import { ErrorBoundary } from "@/components/error-boundary"
import { Toaster } from "sonner"
import { AppLayout } from "@/components/layout/app-layout"
import { ReportManagerPage } from "@/pages/report-manager-page"
import { AuthCallback } from "@/components/auth/auth-callback"
import { RouteAnalytics } from "@/components/RouteAnalytics"
import { usePageTracking } from "@/hooks/useAppInsights"
import { appInsightsService } from "@/services/app-insights"

// Lazy-load the preview page (includes heavy PDF viewer library)
const ReportPreviewPage = lazy(() =>
  import("@/pages/report-preview-page").then((m) => ({
    default: m.ReportPreviewPage,
  })),
)

function PageLoader() {
  const { t } = useTranslation()
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-muted-foreground">{t("common.loading")}</div>
    </div>
  )
}

function AppRoutes() {
  // Track page views automatically on route changes
  usePageTracking()

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/signin-callback" element={<AuthCallback />} />
        <Route element={<AppLayout />}>
          <Route index element={<ReportManagerPage />} />
          <Route path=":projectNumber" element={<ReportManagerPage />} />
        </Route>
        <Route path="ReportPreview" element={<ReportPreviewPage />} />
      </Routes>
    </Suspense>
  )
}

export function App() {
  const handleError = (error: Error) => {
    // Track exceptions to Application Insights
    appInsightsService.trackException(error, 3) // Severity 3 = Error
  }

  return (
    <AuthProvider>
      <QueryProvider>
        <ErrorBoundary onError={handleError}>
          <BrowserRouter>
            <RouteAnalytics />
            <AppRoutes />
          </BrowserRouter>
        </ErrorBoundary>
        <Toaster position="top-center" />
      </QueryProvider>
    </AuthProvider>
  )
}

export default App
